import { useEffect, useState } from 'react';
import { api, ApiRequestError } from '../../api/client.js';

/** Kleine, wiederverwendete Bausteine des Adminbereichs. Kein Framework. */

export function useLoad<T>(
  path: string | null,
  deps: readonly unknown[] = [],
): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (path === null) return;
    let aktiv = true;
    setLoading(true);
    api
      .get<T>(path)
      .then((d) => aktiv && setData(d))
      .catch((e: unknown) => aktiv && setError(fehlertext(e)))
      .finally(() => aktiv && setLoading(false));
    return () => {
      aktiv = false;
    };
    // `deps` kommt vom Aufrufer; er nennt, was einen Neuladevorgang ausloesen soll.
  }, [path, nonce, ...deps]);
  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

export function fehlertext(e: unknown): string {
  return e instanceof ApiRequestError ? e.message : 'Die API ist nicht erreichbar.';
}

export function PageHeader({
  title,
  lead,
  actions,
}: {
  title: string;
  lead?: string;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="page-header">
      <div>
        <h2>{title}</h2>
        {lead !== undefined && <p className="lead">{lead}</p>}
      </div>
      {actions !== undefined && <div className="row">{actions}</div>}
    </div>
  );
}

export function Alert({ text }: { text: string | null }): React.JSX.Element | null {
  return text === null ? null : <p role="alert">{text}</p>;
}

export function Notice({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="notice">{children}</div>;
}

export function StatusPill({
  active,
  activeText = 'aktiv',
  inactiveText = 'inaktiv',
}: {
  active: boolean;
  activeText?: string;
  inactiveText?: string;
}): React.JSX.Element {
  return (
    <span className={active ? 'pill pill-ok' : 'pill pill-off'}>
      {active ? activeText : inactiveText}
    </span>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint !== undefined && <span className="note">{hint}</span>}
    </label>
  );
}

export function eur(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '–';
  return `${Number(v).toFixed(2).replace('.', ',')} €`;
}

export function pct(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '–';
  const n = Number(v);
  return `${Number.isInteger(n) ? n : n.toFixed(2).replace('.', ',')} %`;
}

export function datum(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '–';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}
